import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from '~/timeInit';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  type SxProps,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import { Reservation } from '@luxuryescapes/contract-svc-reservation';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { LUXURY_ESCAPES } from '~/consts/brands';

import {
  useApproveExtranetChange,
  useBulkApproveExtranetChanges,
  useBulkRejectExtranetChanges,
  useGetExtranetPendingChange,
  useGetExtranetPendingChanges,
  useRejectExtranetChange,
} from '~/hooks/apis/extranet/useExtranetPendingChanges';
import { useGetPropertiesByIds } from '~/hooks/apis/reservation/useGetPropertiesByIds';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import { useGetVendorsByIds } from '~/hooks/apis/vendors/useGetVendorsByIds';
import useQuery from '~/hooks/useQuery';

import {
  ExtranetPendingChange,
  ExtranetPendingChangeRequestType,
  ExtranetPendingChangeStatus,
  ListExtranetPendingChangesParams,
} from '~/services/ExtranetService';

import ApiError from '~/types/ApiError';

import ValueDiff from './ValueDiff';
import {
  REQUEST_TYPE_DETAIL_LABELS,
  getExtranetApprovalFilterOptions,
  getExtranetApprovalLabel,
  getExtranetApprovalSectionLabel,
} from './extranetApprovalLabels';

const REQUEST_TYPE_OPTIONS = getExtranetApprovalFilterOptions();

const TAB_CHIP_COLORS: Record<string, SxProps<Theme>> = {
  Rooms: { bgcolor: 'info.light', color: 'info.contrastText' },
  Description: { bgcolor: 'secondary.light', color: 'secondary.contrastText' },
  Policies: { bgcolor: 'warning.light', color: 'warning.contrastText' },
  'Property Details': { bgcolor: 'primary.light', color: 'primary.contrastText' },
  Taxes: { bgcolor: 'success.light', color: 'success.contrastText' },
  Facilities: { bgcolor: 'info.dark', color: 'info.contrastText' },
  Images: { bgcolor: 'error.light', color: 'error.contrastText' },
};

const STATUS_CHIP_COLOR: Record<ExtranetPendingChangeStatus, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  superseded: 'default',
};

const HEADER_CELL_SX: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'text.secondary',
};

const PAGE_SIZE = 50;
const PENDING_TAB = 0;
const HOMES_VILLAS_PENDING_TAB = 1;
const HISTORY_TAB = 2;
const EMPTY_USER_SUMMARIES = new Map<string, App.UserSummary>();

interface Filters {
  status: string;
  vendor_id: string;
  request_type: string;
  property_id: string;
  property_category: string;
}

interface ChangeMetadata {
  vendorName?: string;
  propertyName?: string;
  roomName?: string;
  ratePlanName?: string;
  requestedByEmail?: string;
  requestedByName?: string;
  reviewedByEmail?: string;
  reviewedByName?: string;
}

const DEFAULT_FILTERS: Filters = {
  status: 'pending',
  vendor_id: '',
  request_type: '',
  property_id: '',
  property_category: '',
};

function uniqueSorted(values: Array<string | null | undefined>) {
  return Array.from(
    new Set(values.filter((candidateValue): candidateValue is string => Boolean(candidateValue))),
  ).sort();
}

function getDisplayName(entity: unknown): string | undefined {
  if (!entity || typeof entity !== 'object') return undefined;
  const record = entity as Record<string, unknown>;
  const displayName = record.name ?? record.account_name ?? record.property_name ?? record.title;
  return typeof displayName === 'string' && displayName.trim() ? displayName : undefined;
}

function getRoomMetadata(property: Reservation.Property | undefined, targetIds?: Record<string, unknown> | null) {
  if (!property || !targetIds) return {};
  const roomTypes = property.room_types ?? [];
  const roomTypeId = getTargetIdValue(targetIds, 'room_type_id');
  const roomRateId = getTargetIdValue(targetIds, 'room_rate_id');
  const ratePlanId = getTargetIdValue(targetIds, 'rate_plan_id');

  for (const roomType of roomTypes) {
    const roomRates = roomType.room_rates ?? [];
    const matchedRoomRate = roomRates.find(
      (roomRate) => String(roomRate.id) === String(roomRateId) || String(roomRate.rate_plan?.id) === String(ratePlanId),
    );

    if (String(roomType.id) === String(roomTypeId) || matchedRoomRate) {
      return {
        roomName: typeof roomType.name === 'string' ? roomType.name : undefined,
        ratePlanName: typeof matchedRoomRate?.rate_plan?.name === 'string' ? matchedRoomRate.rate_plan.name : undefined,
      };
    }
  }

  return {};
}

function getUserDisplay(user: App.UserSummary | undefined) {
  return {
    email: user?.email || undefined,
    name: user?.full_name || undefined,
  };
}

const EMPTY_USER_DISPLAY = getUserDisplay(undefined);

function getApiErrorMessage(err: unknown, actionType: 'approve' | 'reject') {
  if (err instanceof ApiError) {
    const responseBody = err.responseBody as Record<string, any> | undefined;
    const replayDetail = responseBody?.replayDetail;
    const replayMessage =
      responseBody?.replayError ||
      replayDetail?.message ||
      replayDetail?.error?.message ||
      (Array.isArray(replayDetail?.errors) ? replayDetail.errors.join(', ') : undefined);

    if (replayMessage) {
      return `Failed to ${actionType}: ${replayMessage}`;
    }

    if (err.status === 409) {
      return `Already processed: ${err.message.replace(/^Network error received \(status code \d+\): /, '')}`;
    }
  }

  if (err instanceof Error) {
    return `Failed to ${actionType}: ${err.message}`;
  }

  return `Failed to ${actionType}`;
}

function getQueryParam(query: URLSearchParams, ...names: string[]) {
  for (const name of names) {
    const value = query.get(name);
    if (value) return value;
  }
  return '';
}

function getTabFromQuery(tab: string) {
  if (tab === 'homes-villas-pending') return HOMES_VILLAS_PENDING_TAB;
  return tab === 'history' ? HISTORY_TAB : PENDING_TAB;
}

function getTabQueryValue(activeTab: number) {
  if (activeTab === HOMES_VILLAS_PENDING_TAB) return 'homes-villas-pending';
  return activeTab === HISTORY_TAB ? 'history' : 'pending';
}

function getDefaultStatusForTab(activeTab: number) {
  return activeTab === HISTORY_TAB ? '' : 'pending';
}

function getDefaultPropertyCategoryForTab(activeTab: number) {
  return activeTab === HOMES_VILLAS_PENDING_TAB ? 'Homes & Villas' : '';
}

function buildFilterSearch(filters: Filters, activeTab: number, changeId?: string) {
  const params = new URLSearchParams({ tab: getTabQueryValue(activeTab) });
  if (filters.vendor_id) params.set('vendor_id', filters.vendor_id);
  if (filters.property_id) params.set('property_id', filters.property_id);
  if (activeTab === HISTORY_TAB && filters.request_type) params.set('request_type', filters.request_type);
  if (activeTab === HISTORY_TAB && filters.status) params.set('status', filters.status);
  if (changeId) params.set('changeId', changeId);
  return `?${params.toString()}`;
}

function getTabFromChangeStatus(status: ExtranetPendingChangeStatus) {
  return status === 'pending' ? PENDING_TAB : HISTORY_TAB;
}

function getFiltersFromQuery(query: URLSearchParams, activeTab: number): Filters {
  return {
    ...DEFAULT_FILTERS,
    status: activeTab === HISTORY_TAB ? getQueryParam(query, 'status') : getDefaultStatusForTab(activeTab),
    vendor_id: getQueryParam(query, 'vendor_id', 'vendorId'),
    property_id: getQueryParam(query, 'property_id', 'propertyId'),
    request_type: activeTab === HISTORY_TAB ? getQueryParam(query, 'request_type', 'requestType') : '',
    property_category: getDefaultPropertyCategoryForTab(activeTab),
  };
}

function buildParams(filters: Filters, page: number, statusOverride?: string): ListExtranetPendingChangesParams {
  const params: ListExtranetPendingChangesParams = { limit: PAGE_SIZE, page };
  const status = statusOverride ?? filters.status;
  if (status) params.status = status;
  if (filters.vendor_id) params.vendor_id = filters.vendor_id;
  if (filters.request_type) params.request_type = filters.request_type as ExtranetPendingChangeRequestType;
  if (filters.property_id) params.property_id = filters.property_id;
  if (filters.property_category) params.propertyCategory = filters.property_category;
  return params;
}

function getTargetIdValue(targetIds: Record<string, unknown> | null | undefined, key: string) {
  const value = targetIds?.[key];
  return typeof value === 'string' ? value : '';
}

function StatCard({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 160 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} mt={0.5}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

function FilterToolbar({
  filters,
  setFilters,
  showRequestTypeFilter,
  showStatusFilter,
  onSearch,
  onClear,
}: {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  showRequestTypeFilter: boolean;
  showStatusFilter: boolean;
  onSearch: () => void;
  onClear: () => void;
}) {
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="flex-end" mb={2}>
      <TextField
        size="small"
        label="Partner ID"
        value={filters.vendor_id}
        onChange={(e) => setFilters((f) => ({ ...f, vendor_id: e.target.value }))}
        sx={{ width: 180 }}
      />
      <TextField
        size="small"
        label="Property ID"
        value={filters.property_id}
        onChange={(e) => setFilters((f) => ({ ...f, property_id: e.target.value }))}
        sx={{ width: 160 }}
      />
      {showRequestTypeFilter && (
        <FormControl size="small" sx={{ minWidth: 210 }}>
          <InputLabel id="extranet-approval-request-type-filter-label">Change type</InputLabel>
          <Select
            labelId="extranet-approval-request-type-filter-label"
            id="extranet-approval-request-type-filter"
            label="Change type"
            value={filters.request_type}
            onChange={(e) => setFilters((f) => ({ ...f, request_type: e.target.value }))}
            inputProps={{ 'data-testid': 'extranet-approval-request-type-filter-input' }}
          >
            <MenuItem value="">All change types</MenuItem>
            {REQUEST_TYPE_OPTIONS.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {showStatusFilter && (
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="extranet-approval-status-filter-label">Status</InputLabel>
          <Select
            labelId="extranet-approval-status-filter-label"
            id="extranet-approval-status-filter"
            label="Status"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            inputProps={{ 'data-testid': 'extranet-approval-status-filter-input' }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="superseded">Superseded</MenuItem>
          </Select>
        </FormControl>
      )}
      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={onSearch}>
          Search
        </Button>
        <Button variant="text" onClick={onClear}>
          Clear
        </Button>
      </Stack>
    </Stack>
  );
}

function ChangeRow({
  row,
  metadata,
  showActions,
  showStatus,
  selected,
  isMutating,
  onToggleSelect,
  onViewDiff,
  onAction,
}: {
  row: ExtranetPendingChange;
  metadata?: ChangeMetadata;
  showActions: boolean;
  showStatus: boolean;
  selected: boolean;
  isMutating: boolean;
  onToggleSelect: () => void;
  onViewDiff: () => void;
  onAction: (type: 'approve' | 'reject') => void;
}) {
  const tabLabel = getExtranetApprovalLabel(row);
  const tabStyle = TAB_CHIP_COLORS[tabLabel] ?? {};
  const propertyId = getTargetIdValue(row.target_ids, 'property_id');
  const roomContext = [metadata?.roomName, metadata?.ratePlanName].filter(Boolean).join(' · ');
  const requestedByLabel = metadata?.requestedByEmail ?? row.requested_by_user_id;
  const requestedByName = metadata?.requestedByName;
  const reviewedByLabel = metadata?.reviewedByEmail ?? row.reviewed_by_user_id;
  const reviewedByName = metadata?.reviewedByName;

  return (
    <TableRow hover sx={{ verticalAlign: 'middle' }}>
      {showActions && (
        <TableCell padding="checkbox" sx={{ py: 1 }}>
          {row.status === 'pending' && <Checkbox checked={selected} onChange={onToggleSelect} size="small" />}
        </TableCell>
      )}
      <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
        <Typography variant="body2">{dayjs(row.created_at).format('D/M/YY, h:mm a')}</Typography>
      </TableCell>
      <TableCell sx={{ py: 1, overflow: 'hidden' }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.3 }}>
          {metadata?.vendorName ?? row.fk_vendor_id}
        </Typography>
        {metadata?.vendorName && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.3, display: 'block' }}>
            {row.fk_vendor_id}
          </Typography>
        )}
        <Tooltip title={propertyId ?? ''}>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.3, display: 'block' }}>
            {metadata?.propertyName ?? propertyId ?? '—'}
          </Typography>
        </Tooltip>
        {roomContext && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.3, display: 'block' }}>
            {roomContext}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 1, overflow: 'hidden' }}>
        <Tooltip title={row.requested_by_user_id}>
          <Typography variant="body2" noWrap sx={{ fontSize: 12 }}>
            {requestedByLabel}
          </Typography>
        </Tooltip>
        {requestedByName && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {requestedByName}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 1 }}>
        <Chip size="small" label={tabLabel} sx={{ ...tabStyle, fontWeight: 600, fontSize: 11 }} />
      </TableCell>
      <TableCell sx={{ maxWidth: 400, py: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              maxHeight: 80,
              overflow: 'auto',
              flex: 1,
            }}
          >
            <ValueDiff previous={row.previous_value} proposed={row.proposed_value} compact />
          </Box>
          <Button
            size="small"
            variant="text"
            sx={{ px: 0, textTransform: 'uppercase', fontSize: 11, whiteSpace: 'nowrap', minWidth: 'auto' }}
            onClick={onViewDiff}
          >
            View diff
          </Button>
        </Stack>
      </TableCell>
      {showActions && (
        <TableCell sx={{ py: 1 }}>
          {row.status === 'pending' ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button size="small" variant="contained" disabled={isMutating} onClick={() => onAction('approve')}>
                Approve
              </Button>
              <Button
                size="small"
                variant="text"
                color="error"
                disabled={isMutating}
                onClick={() => onAction('reject')}
              >
                Reject
              </Button>
            </Stack>
          ) : (
            <Chip size="small" label={row.status} color={STATUS_CHIP_COLOR[row.status]} />
          )}
        </TableCell>
      )}
      {showStatus && (
        <TableCell sx={{ py: 1, overflow: 'hidden' }}>
          <Chip size="small" label={row.status} color={STATUS_CHIP_COLOR[row.status]} />
          {row.reviewed_by_user_id && (
            <Tooltip title={row.reviewed_by_user_id}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                noWrap
                sx={{ fontSize: 10, mt: 0.5 }}
              >
                by {reviewedByLabel}
              </Typography>
            </Tooltip>
          )}
          {reviewedByName && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ fontSize: 10 }}>
              {reviewedByName}
            </Typography>
          )}
          {row.reviewed_at && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10 }}>
              {dayjs(row.reviewed_at).format('D/M/YY')}
            </Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

function ConfirmActionDialog({
  action,
  isMutating,
  onClose,
  onConfirm,
}: {
  action: { type: 'approve' | 'reject'; ids: string[] } | null;
  isMutating: boolean;
  onClose: () => void;
  onConfirm: (action: { type: 'approve' | 'reject'; ids: string[] }) => void;
}) {
  if (!action) return null;
  const isApprove = action.type === 'approve';
  const label = isApprove ? 'Approve' : 'Reject';
  const count = action.ids.length;

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Confirm {isApprove ? 'Approval' : 'Rejection'}</DialogTitle>
      <DialogContent>
        <Typography>
          {label} {count === 1 ? 'this change' : `${count} changes`}?
          {isApprove &&
            ` Approving applies ${count === 1 ? 'this change' : `${count} changes`} to the live listing and records ${
              count === 1 ? 'it' : 'them'
            } as approved.`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isApprove ? 'primary' : 'error'}
          disabled={isMutating}
          onClick={() => onConfirm(action)}
          startIcon={isMutating ? <CircularProgress size={14} /> : undefined}
        >
          {label}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ExtranetApprovalsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const query = useQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = getTabFromQuery(getQueryParam(query, 'tab'));
  const initialFilters = getFiltersFromQuery(query, initialTab);
  const deepLinkedChangeId = getQueryParam(query, 'changeId', 'change_id');
  const openedDeepLinkChangeIdRef = useRef<string | null>(null);
  const skipNextQuerySyncRef = useRef(false);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<ListExtranetPendingChangesParams>(
    buildParams(initialFilters, 1),
  );
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [diffRow, setDiffRow] = useState<ExtranetPendingChange | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'approve' | 'reject'; ids: string[] } | null>(null);

  const { data, isLoading, error } = useGetExtranetPendingChanges(appliedFilters);
  const { data: pendingCountData } = useGetExtranetPendingChanges({ status: 'pending', limit: 0 });
  const { data: homesVillasPendingCountData } = useGetExtranetPendingChanges({
    status: 'pending',
    propertyCategory: 'Homes & Villas',
    limit: 0,
  });
  const { data: deepLinkedChangeData } = useGetExtranetPendingChange(deepLinkedChangeId);

  const isPendingTab = activeTab !== HISTORY_TAB;
  const isHistoryTab = activeTab === HISTORY_TAB;

  useEffect(() => {
    if (skipNextQuerySyncRef.current) {
      skipNextQuerySyncRef.current = false;
      return;
    }

    const nextTab = getTabFromQuery(getQueryParam(query, 'tab'));
    const nextFilters = getFiltersFromQuery(query, nextTab);
    setActiveTab(nextTab);
    setFilters(nextFilters);
    setPage(1);
    setSelected(new Set());
    setAppliedFilters(buildParams(nextFilters, 1));
  }, [query]);

  const syncUrlFilters = useCallback(
    (nextFilters: Filters, nextTab: number, options: { changeId?: string; replace?: boolean } = {}) => {
      const search = buildFilterSearch(nextFilters, nextTab, options.changeId);
      if (search === location.search) return;

      skipNextQuerySyncRef.current = true;
      navigate({ pathname: location.pathname, search }, { replace: options.replace ?? true });
    },
    [location.pathname, location.search, navigate],
  );

  useEffect(() => {
    const deepLinkedChange = deepLinkedChangeData?.result;
    if (!deepLinkedChangeId || !deepLinkedChange || openedDeepLinkChangeIdRef.current === deepLinkedChangeId) return;

    const nextTab = getTabFromChangeStatus(deepLinkedChange.status);
    const nextFilters = {
      ...getFiltersFromQuery(query, nextTab),
      vendor_id: getQueryParam(query, 'vendor_id', 'vendorId') || deepLinkedChange.fk_vendor_id,
      property_id:
        getQueryParam(query, 'property_id', 'propertyId') ||
        getTargetIdValue(deepLinkedChange.target_ids, 'property_id') ||
        '',
    };

    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
      setFilters(nextFilters);
      setPage(1);
      setSelected(new Set());
      setAppliedFilters(buildParams(nextFilters, 1));
      syncUrlFilters(nextFilters, nextTab, { changeId: deepLinkedChangeId, replace: true });
    }

    setDiffRow(deepLinkedChange);
    openedDeepLinkChangeIdRef.current = deepLinkedChangeId;
  }, [activeTab, deepLinkedChangeData, deepLinkedChangeId, query, syncUrlFilters]);

  const approveOne = useApproveExtranetChange();
  const rejectOne = useRejectExtranetChange();
  const bulkApprove = useBulkApproveExtranetChanges();
  const bulkReject = useBulkRejectExtranetChanges();

  const rows = data?.result ?? [];
  const total = data?.total ?? 0;
  const selectableRows = isPendingTab ? rows : [];

  const handleSearch = useCallback(() => {
    setPage(1);
    setSelected(new Set());
    setAppliedFilters(buildParams(filters, 1));
    syncUrlFilters(filters, activeTab);
  }, [activeTab, filters, syncUrlFilters]);

  const handleClear = useCallback(() => {
    const resetFilters = {
      ...DEFAULT_FILTERS,
      status: getDefaultStatusForTab(activeTab),
      property_category: getDefaultPropertyCategoryForTab(activeTab),
    };
    setFilters(resetFilters);
    setPage(1);
    setSelected(new Set());
    setAppliedFilters(buildParams(resetFilters, 1));
    syncUrlFilters(resetFilters, activeTab);
  }, [activeTab, syncUrlFilters]);

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === selectableRows.length) return new Set();
      return new Set(selectableRows.map((r) => r.id));
    });
  }, [selectableRows]);

  const handleConfirmAction = useCallback(
    async (action: { type: 'approve' | 'reject'; ids: string[] }) => {
      const { type, ids } = action;
      setConfirmAction(null);

      try {
        if (ids.length === 1) {
          if (type === 'approve') {
            await approveOne.mutateAsync(ids[0]);
          } else {
            await rejectOne.mutateAsync(ids[0]);
          }
          enqueueSnackbar(`Change ${type}d`, { variant: 'success' });
        } else if (type === 'approve') {
          const result = await bulkApprove.mutateAsync(ids);
          enqueueSnackbar(`${result.approved} change(s) approved, ${result.skipped} skipped`, { variant: 'success' });
        } else {
          const result = await bulkReject.mutateAsync(ids);
          enqueueSnackbar(`${result.rejected} change(s) rejected, ${result.skipped} skipped`, { variant: 'success' });
        }
        setSelected(new Set());
      } catch (err) {
        enqueueSnackbar(getApiErrorMessage(err, type), {
          variant: err instanceof ApiError && err.status === 409 ? 'warning' : 'error',
        });
      }
    },
    [approveOne, rejectOne, bulkApprove, bulkReject, enqueueSnackbar],
  );

  const isMutating = approveOne.isPending || rejectOne.isPending || bulkApprove.isPending || bulkReject.isPending;
  const selectedIds = Array.from(selected);
  const allPendingSelected = selectableRows.length > 0 && selected.size === selectableRows.length;
  const colCount = isPendingTab ? 7 : 6;
  const diffSectionLabel = diffRow ? getExtranetApprovalSectionLabel(diffRow.target_ids) : undefined;
  const visibleChanges = useMemo(() => {
    const changes = [...rows];
    const deepLinkedChange = deepLinkedChangeData?.result;
    if (deepLinkedChange && !changes.some((row) => row.id === deepLinkedChange.id)) {
      changes.push(deepLinkedChange);
    }
    return changes;
  }, [deepLinkedChangeData, rows]);
  const vendorIds = useMemo(() => uniqueSorted(visibleChanges.map((row) => row.fk_vendor_id)), [visibleChanges]);
  const propertyIds = useMemo(
    () => uniqueSorted(visibleChanges.map((row) => getTargetIdValue(row.target_ids, 'property_id'))),
    [visibleChanges],
  );
  const userIds = useMemo(
    () =>
      uniqueSorted(
        visibleChanges.flatMap((row) => [row.requested_by_user_id, row.reviewed_by_user_id].filter(Boolean)),
      ),
    [visibleChanges],
  );
  const { vendorMap } = useGetVendorsByIds(vendorIds);
  const { propertyMap } = useGetPropertiesByIds(propertyIds);
  const { data: userSummaries = EMPTY_USER_SUMMARIES } = useGetUsersSummaryByIds({
    userIds,
    brand: LUXURY_ESCAPES,
    enabled: userIds.length > 0,
  });
  const metadataByChangeId = useMemo(() => {
    const changeMetadataMap = new Map<string, ChangeMetadata>();

    for (const row of visibleChanges) {
      const propertyId = getTargetIdValue(row.target_ids, 'property_id');
      const property = propertyId ? propertyMap.get(propertyId) : undefined;
      const requestedBy = getUserDisplay(userSummaries.get(row.requested_by_user_id));
      const reviewedBy = row.reviewed_by_user_id
        ? getUserDisplay(userSummaries.get(row.reviewed_by_user_id))
        : EMPTY_USER_DISPLAY;

      changeMetadataMap.set(row.id, {
        vendorName: getDisplayName(vendorMap.get(row.fk_vendor_id)),
        propertyName: getDisplayName(property),
        ...getRoomMetadata(property, row.target_ids),
        requestedByEmail: requestedBy.email,
        requestedByName: requestedBy.name,
        reviewedByEmail: reviewedBy.email,
        reviewedByName: reviewedBy.name,
      });
    }

    return changeMetadataMap;
  }, [propertyMap, userSummaries, vendorMap, visibleChanges]);
  const diffRowMetadata = diffRow ? metadataByChangeId.get(diffRow.id) : undefined;
  const diffRowPropertyId = diffRow ? getTargetIdValue(diffRow.target_ids, 'property_id') : '';

  return (
    <Box>
      <PageSubheader title="Extranet — change approvals" />

      <Typography variant="body2" color="text.secondary" mb={2} maxWidth={680}>
        Review pending Extranet edits submitted by partners across Rooms, Description, Policies, Property Details,
        Taxes, Facilities, and Images. Approve applies accepted changes to the live listing and records them as
        approved.
      </Typography>

      <Stack direction="row" spacing={2} flexWrap="wrap" mb={3}>
        <StatCard label="Pending changes" value={pendingCountData?.total ?? 0} hint="All Extranet tabs" />
        <StatCard
          label="Homes & Villas pending"
          value={homesVillasPendingCountData?.total ?? 0}
          hint="Dedicated queue"
        />
      </Stack>

      <Tabs
        value={activeTab}
        onChange={(_, v) => {
          setActiveTab(v);
          setPage(1);
          setSelected(new Set());
          const resetFilters = {
            ...DEFAULT_FILTERS,
            status: getDefaultStatusForTab(v),
            property_category: getDefaultPropertyCategoryForTab(v),
          };
          setFilters(resetFilters);
          setAppliedFilters(buildParams(resetFilters, 1));
          syncUrlFilters(resetFilters, v);
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Pending approvals" />
        <Tab label="Homes & Villas pending" />
        <Tab label="History" />
      </Tabs>

      <FilterToolbar
        filters={filters}
        setFilters={setFilters}
        showRequestTypeFilter={isHistoryTab}
        showStatusFilter={isHistoryTab}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      {selectedIds.length > 0 && (
        <Stack direction="row" spacing={1} mb={1.5} alignItems="center">
          <Typography variant="body2">{selectedIds.length} selected</Typography>
          <Button
            size="small"
            variant="contained"
            disabled={isMutating}
            onClick={() => setConfirmAction({ type: 'approve', ids: selectedIds })}
          >
            Bulk approve ({selectedIds.length})
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            disabled={isMutating}
            onClick={() => setConfirmAction({ type: 'reject', ids: selectedIds })}
          >
            Bulk reject
          </Button>
        </Stack>
      )}

      {error instanceof Error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              {isPendingTab && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allPendingSelected}
                    indeterminate={selected.size > 0 && !allPendingSelected}
                    onChange={toggleSelectAll}
                    inputProps={{ 'aria-label': 'Select all pending changes' }}
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                  />
                </TableCell>
              )}
              <TableCell sx={{ ...HEADER_CELL_SX, width: 120 }}>Submitted</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, width: '18%' }}>Partner / Property</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, width: '18%' }}>Changed by</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, width: 70 }}>Tab</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Change (old → new)</TableCell>
              {isPendingTab && <TableCell sx={{ ...HEADER_CELL_SX, width: 160 }}>Action</TableCell>}
              {isHistoryTab && <TableCell sx={{ ...HEADER_CELL_SX, width: 160 }}>Status</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No records found
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <ChangeRow
                key={row.id}
                row={row}
                metadata={metadataByChangeId.get(row.id)}
                showActions={isPendingTab}
                showStatus={isHistoryTab}
                selected={selected.has(row.id)}
                isMutating={isMutating}
                onToggleSelect={() => toggleSelect(row.id)}
                onViewDiff={() => setDiffRow(row)}
                onAction={(type) => setConfirmAction({ type, ids: [row.id] })}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        onPageChange={(_, p) => {
          const nextPage = p + 1;
          setPage(nextPage);
          setSelected(new Set());
          setAppliedFilters((prev) => ({ ...prev, page: nextPage }));
        }}
      />

      <Dialog open={!!diffRow} onClose={() => setDiffRow(null)} maxWidth="md" fullWidth>
        <DialogTitle>Change diff — {diffRow && getExtranetApprovalLabel(diffRow)}</DialogTitle>
        {diffRow && (
          <Box sx={{ px: 3, pb: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Partner
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {diffRowMetadata?.vendorName ?? diffRow.fk_vendor_id}
              </Typography>
              {diffRowMetadata?.vendorName && (
                <Typography variant="caption" color="text.secondary">
                  {diffRow.fk_vendor_id}
                </Typography>
              )}
            </Box>
            {diffRowPropertyId && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Property
                </Typography>
                <Typography variant="body2">{diffRowMetadata?.propertyName ?? diffRowPropertyId}</Typography>
                {diffRowMetadata?.propertyName && (
                  <Typography variant="caption" color="text.secondary">
                    {diffRowPropertyId}
                  </Typography>
                )}
              </Box>
            )}
            {(diffRowMetadata?.roomName || diffRowMetadata?.ratePlanName) && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Room
                </Typography>
                <Typography variant="body2">
                  {[diffRowMetadata.roomName, diffRowMetadata.ratePlanName].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            )}
            {diffSectionLabel && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Section
                </Typography>
                <Typography variant="body2">{diffSectionLabel}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Change type
              </Typography>
              <Typography variant="body2">
                {REQUEST_TYPE_DETAIL_LABELS[diffRow.request_type] ?? diffRow.request_type}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Submitted
              </Typography>
              <Typography variant="body2">{dayjs(diffRow.created_at).format('D/M/YY, h:mm a')}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Changed by
              </Typography>
              <Typography variant="body2">
                {diffRowMetadata?.requestedByEmail ?? diffRow.requested_by_user_id}
              </Typography>
              {diffRowMetadata?.requestedByName && (
                <Typography variant="caption" color="text.secondary">
                  {diffRowMetadata.requestedByName}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box>
                <Chip size="small" label={diffRow.status} color={STATUS_CHIP_COLOR[diffRow.status]} />
              </Box>
            </Box>
            {diffRow.reviewed_by_user_id && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {diffRow.status === 'approved' ? 'Approved by' : 'Reviewed by'}
                </Typography>
                <Typography variant="body2">
                  {diffRowMetadata?.reviewedByEmail ?? diffRow.reviewed_by_user_id}
                </Typography>
                {diffRowMetadata?.reviewedByName && (
                  <Typography variant="caption" color="text.secondary">
                    {diffRowMetadata.reviewedByName}
                  </Typography>
                )}
              </Box>
            )}
            {diffRow.reviewed_at && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reviewed at
                </Typography>
                <Typography variant="body2">{dayjs(diffRow.reviewed_at).format('D/M/YY, h:mm a')}</Typography>
              </Box>
            )}
          </Box>
        )}
        <DialogContent dividers>
          {diffRow && <ValueDiff previous={diffRow.previous_value} proposed={diffRow.proposed_value} />}
        </DialogContent>
        <DialogActions>
          {diffRow?.status === 'pending' && (
            <>
              <Button
                variant="contained"
                onClick={() => {
                  setConfirmAction({ type: 'approve', ids: [diffRow.id] });
                  setDiffRow(null);
                }}
              >
                Approve
              </Button>
              <Button
                variant="text"
                color="error"
                onClick={() => {
                  setConfirmAction({ type: 'reject', ids: [diffRow.id] });
                  setDiffRow(null);
                }}
              >
                Reject
              </Button>
            </>
          )}
          <Button variant="text" onClick={() => setDiffRow(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmActionDialog
        action={confirmAction}
        isMutating={isMutating}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirmAction}
      />
    </Box>
  );
}
